import { createPortal } from "react-dom";
import { GanttBar } from "@/ds";
import type { GanttTone } from "@/ds";
import EstrelaIcon from "@/assets/icons/gantt-estrela.svg?react";
import SetaLimiteIcon from "@/assets/icons/gantt-limite-seta.svg?react";
import { Gantt } from "@/shared/components";
import { REVISAO } from "../../../nova-obra.constants";
import { formatDataBr } from "../../../components/format-data";
import styles from "./revisao-gantt.module.scss";
import type {
  TipoBarra,
  CardEmFoco,
  BarraProps,
  RevisaoGanttViewModel,
} from "./types-revisao-gantt";

const ROTULO_CATEGORIA: Record<TipoBarra, string> = {
  navegacao: REVISAO.duracao.legendaNavegacao,
  permanencia: REVISAO.duracao.legendaPermanencia,
  comissionamento: REVISAO.duracao.legendaComissionamento,
  obra: REVISAO.duracao.legendaObra,
  total: REVISAO.duracao.legendaTotal,
};

// As classes do módulo seguem `localsConvention: "camelCaseOnly"`: chaves montadas em runtime não casam.
const CLASSE_TIPO = {
  navegacao: "tipoNavegacao",
  permanencia: "tipoPermanencia",
  comissionamento: "tipoComissionamento",
  obra: "tipoObra",
  total: "tipoTotal",
} as const satisfies Record<TipoBarra, string>;

/** Tom do átomo do DS; a cor do Figma (regras 12223:137954) vem da classe do tipo. */
const TOM_TIPO: Record<TipoBarra, GanttTone> = {
  navegacao: "info",
  permanencia: "info",
  comissionamento: "success",
  obra: "primary",
  total: "primary",
};

const dias = (total: number) => REVISAO.duracao.dias(total);

/** Card ao passar sobre a barra (Figma `tooltip-gantt-periodo` 11110:56949), no body. */
function CardBarra({ dados, estilo }: CardEmFoco) {
  return createPortal(
    <div role="tooltip" className={styles.card} style={estilo} data-testid="revisao-gantt-card">
      <div className={styles.cardTitulo}>
        <p className={styles.cardNome}>{dados.titulo}</p>
        <p className={styles.cardApoio}>
          {ROTULO_CATEGORIA[dados.tipo]} · {REVISAO.duracao.tipPlanejado}
        </p>
      </div>
      <div className={styles.cardFases}>
        <div className={styles.cardLinha}>
          <span
            className={`${styles.cardAmostra} ${styles[CLASSE_TIPO[dados.tipo]]}`}
            aria-hidden
          />
          <span className={styles.cardRotuloPeriodo}>{REVISAO.duracao.tipPeriodo}</span>
          <span className={styles.cardValor}>
            {formatDataBr(dados.inicio)} – {formatDataBr(dados.fim)}
          </span>
          <span className={styles.cardDias}>{dias(dados.dias)}</span>
        </div>
        <div className={styles.cardLinha}>
          <span className={styles.cardRotuloMeta}>{REVISAO.duracao.tipMeta}</span>
          <span className={styles.cardValor}>
            {dados.meta === null ? REVISAO.vazio : dias(dados.meta)}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** `duracao/barra-periodo` (10413:96245) sobre o `GanttBar` do DS: cor do tipo, "Nd" dentro ou à direita e o card. */
function Barra({ barra, onFoco, onSair }: BarraProps) {
  return (
    <GanttBar
      start={barra.inicio}
      end={barra.fim}
      tone={TOM_TIPO[barra.tipo]}
      variant="segment"
      label={barra.rotulo}
      className={`${styles.barra} ${styles[CLASSE_TIPO[barra.tipo]]}`}
      classNames={{ label: barra.dentro ? styles.barraDias : styles.barraDiasFora }}
      tabIndex={0}
      aria-label={barra.ariaLabel}
      onMouseEnter={(evento) => onFoco(barra.dados, evento.currentTarget)}
      onFocus={(evento) => onFoco(barra.dados, evento.currentTarget)}
      onMouseLeave={onSair}
      onBlur={onSair}
      testId={barra.testId}
      data-tipo={barra.tipo}
    />
  );
}

function Legenda() {
  const itens: readonly (readonly [TipoBarra, string])[] = [
    ["navegacao", REVISAO.duracao.legendaNavegacao],
    ["permanencia", REVISAO.duracao.legendaPermanencia],
    ["obra", REVISAO.duracao.legendaObra],
    ["comissionamento", REVISAO.duracao.legendaComissionamento],
    ["total", REVISAO.duracao.legendaTotal],
  ];
  return (
    <div className={styles.legenda} data-testid="revisao-gantt-legenda">
      <span className={styles.legendaTitulo}>{REVISAO.duracao.legenda}</span>
      {itens.map(([tipo, texto]) => (
        <span key={tipo} className={styles.legendaItem}>
          <span className={`${styles.legendaAmostra} ${styles[CLASSE_TIPO[tipo]]}`} aria-hidden />
          {texto}
        </span>
      ))}
      <span className={styles.legendaItem}>
        <span className={styles.marcaInicio} aria-hidden>
          <EstrelaIcon />
        </span>
        {REVISAO.duracao.legendaInicio}
      </span>
      <span className={styles.legendaItem}>
        <span className={styles.marcaLimite} aria-hidden>
          <SetaLimiteIcon />
          <i />
        </span>
        {REVISAO.duracao.legendaLimite}
      </span>
    </div>
  );
}

/**
 * Gantt da Revisão (Figma `gantt-parada` 10706:406743; `revisao/linha-do-tempo` 10973-414671; marcos 10413:96256;
 * cores 12223:137954), sobre a base `Gantt` do shared: legenda, meses, Total da parada (com a estrela do início),
 * Período de obra, uma barra por período e uma linha por limite de classe, com a linha tracejada até ela.
 */
export function RevisaoGanttView(vm: RevisaoGanttViewModel) {
  return (
    <div className={styles.gantt} ref={vm.containerRef} data-testid="revisao-timeline-gantt">
      <Legenda />
      <Gantt.Root
        className={styles.quadro}
        classNames={{ header: styles.cabecalho, headerCell: styles.cabecalho }}
        label={REVISAO.duracao.ganttLabel}
        scale={vm.escala}
        header={["month-year"]}
        unitMinWidth={vm.larguraMes}
        columns={[
          {
            key: "periodo",
            header: REVISAO.duracao.colPeriodo,
            width: vm.larguraColuna,
            cellClassName: styles.rotulo,
          },
        ]}
        markers={vm.marcos}
        rows={vm.linhas.map((linha) => ({
          key: linha.key,
          minHeight: vm.alturaLinha,
          cells: [
            <span key="rotulo" className={styles.rotuloTexto} data-destaque={linha.destaque}>
              {linha.rotulo}
            </span>,
          ],
          content:
            linha.tipo === "barra" ? (
              <Barra barra={linha.barra} onFoco={vm.focar} onSair={vm.sair} />
            ) : (
              <span
                className={styles.limiteData}
                style={linha.estiloData}
                data-testid="revisao-gantt-limite-data"
              >
                {linha.data}
              </span>
            ),
        }))}
        testId="revisao-gantt"
      />
      {vm.card && <CardBarra {...vm.card} />}
    </div>
  );
}
